import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import LocationSelect from "@/components/LocationSelect";

const EMPTY = { nome: "", email: "", telefono: "", cf_piva: "", indirizzo: "", provincia: "", comune: "", note: "" };

export default function Clienti() {
  const [clients, setClients] = useState(null);
  const [editing, setEditing] = useState(null); // null=closed, {}=new, client=edit
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => API.get("/clients").then((r) => setClients(r.data)).catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(EMPTY);
    setEditing({});
  };

  const openEdit = (c) => {
    setForm({ nome: c.nome, email: c.email || "", telefono: c.telefono || "", cf_piva: c.cf_piva || "", indirizzo: c.indirizzo || "", provincia: c.provincia || "", comune: c.comune || "", note: c.note || "" });
    setEditing(c);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing?.id) {
        await API.put(`/clients/${editing.id}`, form);
        toast.success("Cliente aggiornato");
      } else {
        await API.post("/clients", form);
        toast.success("Cliente creato");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminare il cliente "${c.nome}"?`)) return;
    try {
      await API.delete(`/clients/${c.id}`);
      toast.success("Cliente eliminato");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="clienti-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-1">Anagrafica</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">Clienti</h1>
        </div>
        <Button onClick={openNew} className="bg-blue-800 hover:bg-blue-900" data-testid="add-cliente-button">
          <Plus className="h-4 w-4 mr-1.5" /> Nuovo Cliente
        </Button>
      </div>

      {clients === null ? (
        <div className="flex justify-center py-20">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-800 border-t-transparent" />
        </div>
      ) : clients.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center" data-testid="clienti-empty">
          <p className="text-slate-500">Nessun cliente in anagrafica.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left">
                <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">Nome</th>
                <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">Contatti</th>
                <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">CF / P.IVA</th>
                <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">Pratiche</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {clients.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50" data-testid="cliente-row">
                  <td className="px-5 py-3.5 font-semibold text-slate-800">{c.nome}</td>
                  <td className="px-5 py-3.5 text-slate-600">
                    <div>{c.email}</div>
                    <div className="text-xs text-slate-400">{c.telefono}</div>
                  </td>
                  <td className="px-5 py-3.5 font-mono text-xs text-slate-600">{c.cf_piva || "—"}</td>
                  <td className="px-5 py-3.5">
                    <span className="font-mono text-xs font-semibold px-2 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-200">
                      {c.num_pratiche}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(c)} className="text-slate-400 hover:text-blue-800 mr-3" data-testid="cliente-edit-button">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => remove(c)} className="text-slate-400 hover:text-red-600" data-testid="cliente-delete-button">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-lg bg-white" data-testid="cliente-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing?.id ? "Modifica Cliente" : "Nuovo Cliente"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-2">
              <Label>Nome e Cognome / Ragione Sociale *</Label>
              <Input required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="cliente-nome-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cliente-email-input" />
              </div>
              <div className="space-y-2">
                <Label>Telefono</Label>
                <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} data-testid="cliente-telefono-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>CF / P.IVA</Label>
                <Input value={form.cf_piva} onChange={(e) => setForm({ ...form, cf_piva: e.target.value })} data-testid="cliente-cf-input" />
              </div>
              <div className="space-y-2">
                <Label>Indirizzo di residenza</Label>
                <Input value={form.indirizzo} onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} data-testid="cliente-indirizzo-input" />
              </div>
            </div>
            <LocationSelect provincia={form.provincia} comune={form.comune} required onChange={(v) => setForm({ ...form, ...v })} />
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="cliente-note-input" />
            </div>
            <Button type="submit" disabled={saving} className="w-full bg-blue-800 hover:bg-blue-900" data-testid="cliente-submit-button">
              {saving ? "Salvataggio..." : "Salva Cliente"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
